/**
 * wordOpsLogger — ring buffer + pub-sub of every Word.run firing in
 * the add-in, plus a ``withWordOpLog`` helper that wraps a Word.run-
 * using function with auto-timing.
 *
 * # Why this exists
 *
 * Word's Office.js bridge is the dominant performance cost in the
 * add-in. Until now there was no way to *see* when a Word.run fired,
 * how long it took, or which selection / cursor change caused it.
 * Hangs on long CSR drafts were diagnosed by guessing. This module
 * surfaces every bridge round-trip so they can be inspected live.
 *
 * # Mechanism
 *
 *   1. ``logWordOp(entry)`` pushes into a ring buffer (last
 *      ``BUFFER_SIZE`` entries) and notifies all subscribers.
 *   2. ``withWordOpLog(name, fn, detailsFromResult?)`` wraps a Word
 *      operation: starts a perf timer, awaits the work, computes
 *      duration, optionally extracts details from the result, then
 *      ``logWordOp``s the entry. Exceptions are caught, logged with
 *      ``error: true``, and re-thrown.
 *   3. ``WordOpsPanel.tsx`` subscribes and renders the ring buffer
 *      when ``localStorage["asthra:debug:word-ops"] === "1"``.
 *
 * No React and no Office.js import: ``installWordSyncCounter`` patches the
 * ``Word`` global only when called at boot, so tests can drive the rest
 * without a Word host.
 */

import { getLogger } from "../observability";

const BUFFER_SIZE = 200;
const CONSOLE_FLAG = "asthra:debug:word-ops";

export interface WordOpLogEntry {
  /** Epoch milliseconds at completion (or error). */
  ts: number;
  /** Stable name for the op, e.g. "checkDocEmpty", "readSelectionText". */
  name: string;
  /** Wall-clock duration in ms, rounded to two decimals. */
  durationMs: number;
  /** Free-form structured info — e.g. ``{ isEmpty: true }`` or
   *  ``{ branch: "multi-paragraph-short-circuit", paragraphs: 5273 }``. */
  details?: Record<string, unknown>;
  /** ``true`` when the wrapped op threw. ``details.error`` carries the message. */
  error?: boolean;
}

type Listener = (entries: readonly WordOpLogEntry[]) => void;

const buffer: WordOpLogEntry[] = [];
const listeners = new Set<Listener>();

function shouldConsoleLog(): boolean {
  try {
    return globalThis.window !== undefined && globalThis.window.localStorage?.getItem(CONSOLE_FLAG) === "1";
  } catch {
    return false;
  }
}

function notify(): void {
  const snapshot = buffer.slice();
  for (const l of Array.from(listeners)) {
    try {
      l(snapshot);
    } catch (err) {
      console.warn("[wordOpsLogger] listener threw:", err);
    }
  }
}

function shouldAlwaysConsoleLog(entry: WordOpLogEntry): boolean {
  return entry.name.startsWith("chipVisibility:");
}

/** Push an entry into the ring buffer and notify subscribers. */
export function logWordOp(entry: WordOpLogEntry): void {
  buffer.push(entry);
  if (buffer.length > BUFFER_SIZE) buffer.shift();
  if (shouldAlwaysConsoleLog(entry) || shouldConsoleLog()) {
    // eslint-disable-next-line no-console
    console.info(shouldAlwaysConsoleLog(entry) ? "[chip-debug]" : "[word-op]", entry);
  }
  notify();
}

/** Log a zero-duration debug step into the same ring buffer/console path
 *  as Word op timings so event lifecycles can be inspected alongside
 *  Word.run entries. */
export function logWordDebugStep(name: string, details?: Record<string, unknown>): void {
  logWordOp({
    ts: Date.now(),
    name,
    durationMs: 0,
    details,
  });
}

/** Subscribe to the ring buffer. Returns the unsubscribe function.
 *  The listener is called once immediately with the current snapshot. */
export function subscribeWordOps(cb: Listener): () => void {
  listeners.add(cb);
  try {
    cb(buffer.slice());
  } catch (err) {
    console.warn("[wordOpsLogger] initial listener call threw:", err);
  }
  return () => {
    listeners.delete(cb);
  };
}

/** Read-only snapshot of the current ring buffer. */
export function getWordOps(): readonly WordOpLogEntry[] {
  return buffer.slice();
}

/** Clear the ring buffer. Notifies subscribers. */
export function clearWordOps(): void {
  buffer.length = 0;
  notify();
}

/** Test-only — reset module state. */
export function _resetWordOpsForTesting(): void {
  buffer.length = 0;
  listeners.clear();
  _lastReported.clear();
  _activeOps.clear();
  _installed = false;
  _totalSyncs = 0;
  _totalSyncMs = 0;
}

/** Monotonic high-resolution clock in ms (falls back to `Date.now()` where
 *  `performance` is unavailable). Exported so other modules (e.g. the sync
 *  engine's per-phase timing) share one guarded implementation. */
export function nowMs(): number {
  try {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
  } catch {
    // fall through
  }
  return Date.now();
}

function now(): number {
  return nowMs();
}

// ---------------------------------------------------------------------------
// Sync counting — the cost model on Word desktop
// ---------------------------------------------------------------------------
//
// On Word for Mac each ``context.sync()`` is a ~300 ms round trip to Word's UI
// thread (office-js#6721), so an op's cost is roughly its sync count. Every
// op logged here records ``syncs`` / ``syncMs`` alongside its wall-clock
// duration, and slow or failing ops are shipped to the server log so desktop
// numbers are visible without devtools.
//
// Global sync tally. ``installWordSyncCounter`` wraps ``Word.run`` once at boot
// so every request context — including the few callers that skip
// ``withWordOpLog`` — counts its syncs here. ``withWordOpLog`` records the
// delta over its own lifetime; when ops overlap the delta includes the other
// op's syncs, so the entry is marked ``overlapped``.

/** Ops slower than this (or with at least ``SLOW_OP_SYNCS`` syncs) are sent to
 *  the server log. */
const SLOW_OP_MS = 1500;
const SLOW_OP_SYNCS = 15;
/** One server record per op name per window, so a failing poll can't flood. */
const REPORT_WINDOW_MS = 60_000;
const _lastReported = new Map<string, number>();

let _totalSyncs = 0;
let _totalSyncMs = 0;
let _installed = false;
const _activeOps = new Set<{ overlapped: boolean }>();

type SyncableContext = { sync: (...args: never[]) => Promise<unknown> };

/** Wrap ``context.sync`` so every call is counted and timed. Behaviour is
 *  unchanged: the wrapped call resolves and rejects exactly as the original. */
export function instrumentSyncs(context: SyncableContext): void {
  const original = context.sync;
  if (typeof original !== "function") return;
  const bound = original.bind(context) as (...args: unknown[]) => Promise<unknown>;
  (context as { sync: unknown }).sync = async (...args: unknown[]) => {
    const start = now();
    try {
      return await bound(...args);
    } finally {
      _totalSyncs += 1;
      _totalSyncMs += now() - start;
    }
  };
}

/** Patch ``Word.run`` (all overloads: the batch is always the last argument)
 *  so each request context it creates is instrumented. A ``Word.run`` outside
 *  any ``withWordOpLog`` op is logged as ``word.run:anon``. Idempotent. */
export function installWordSyncCounter(): void {
  if (_installed) return;
  const word = (globalThis as { Word?: { run?: (...args: unknown[]) => Promise<unknown> } }).Word;
  if (!word || typeof word.run !== "function") return;
  _installed = true;
  const originalRun = word.run.bind(word);
  word.run = (...args: unknown[]) => {
    const last = args.length - 1;
    const batch = args[last];
    if (typeof batch !== "function") return originalRun(...args);
    const anon = _activeOps.size === 0;
    const before = { syncs: _totalSyncs, syncMs: _totalSyncMs, t: now() };
    args[last] = (context: SyncableContext) => {
      instrumentSyncs(context);
      return (batch as (c: SyncableContext) => unknown)(context);
    };
    const run = originalRun(...args);
    if (!anon) return run;
    const finish = (error: unknown) => {
      logWordOp({
        ts: Date.now(),
        name: "word.run:anon",
        durationMs: Math.round((now() - before.t) * 100) / 100,
        ...(error === undefined ? {} : { error: true }),
        details: {
          syncs: _totalSyncs - before.syncs,
          syncMs: Math.round(_totalSyncMs - before.syncMs),
          ...(error === undefined ? {} : describeWordError(error)),
        },
      });
    };
    return run.then(
      (r) => {
        finish(undefined);
        return r;
      },
      (e: unknown) => {
        finish(e);
        throw e;
      },
    );
  };
}

function _hostInfo(): Record<string, unknown> {
  try {
    const diag = (globalThis as { Office?: { context?: { diagnostics?: { platform?: unknown; version?: unknown } } } })
      .Office?.context?.diagnostics;
    return { platform: diag?.platform, hostVersion: diag?.version };
  } catch {
    return {};
  }
}

/** Office.js errors carry ``code`` and ``debugInfo`` (errorLocation,
 *  statement) — the only clue to WHICH API failed on a desktop build. */
export function describeWordError(err: unknown): Record<string, unknown> {
  if (!(err instanceof Error)) return { error: String(err) };
  const e = err as Error & { code?: unknown; debugInfo?: { errorLocation?: unknown; statement?: unknown } };
  return {
    error: e.message,
    code: e.code,
    errorLocation: e.debugInfo?.errorLocation,
    statement: typeof e.debugInfo?.statement === "string" ? e.debugInfo.statement.slice(0, 300) : undefined,
  };
}

function _shouldReport(key: string): boolean {
  const t = Date.now();
  const last = _lastReported.get(key);
  if (last !== undefined && t - last < REPORT_WINDOW_MS) return false;
  _lastReported.set(key, t);
  return true;
}

/** Report a Word failure that the caller handles (falls back, degrades) so it
 *  still reaches the server log — production strips ``console.warn``, which is
 *  where these used to go. Throttled per ``op``. */
export function reportWordFailure(op: string, err: unknown, extra?: Record<string, unknown>): void {
  if (!_shouldReport(`fail:${op}`)) return;
  getLogger("word.ops").error("word.op.failed", { op, ...describeWordError(err), ...extra, ..._hostInfo() });
}

function _reportIfSlow(name: string, durationMs: number, syncs: number, syncMs: number, overlapped: boolean): void {
  if (durationMs < SLOW_OP_MS && syncs < SLOW_OP_SYNCS) return;
  if (!_shouldReport(`slow:${name}`)) return;
  getLogger("word.ops").info("word.op.slow", { op: name, durationMs, syncs, syncMs, overlapped, ..._hostInfo() });
}

/**
 * Wrap a Word-touching async function with auto-timing and auto-logging.
 *
 * @param name           Stable identifier shown in the panel.
 * @param fn             The work to perform — typically a function that
 *                       runs ``Word.run(...)`` internally.
 * @param detailsFromResult Optional projector from the result into the
 *                       structured ``details`` field. Useful for
 *                       surfacing branch information (``{ branch:
 *                       "isEmpty" }``) or scalar results (``{ isEmpty:
 *                       true }``).
 *
 * Errors are caught, logged with ``error: true``, and re-thrown so
 * callers can apply their own fallbacks.
 */
export async function withWordOpLog<T>(
  name: string,
  fn: () => Promise<T>,
  detailsFromResult?: (result: T) => Record<string, unknown>,
): Promise<T> {
  const start = now();
  const syncsBefore = _totalSyncs;
  const syncMsBefore = _totalSyncMs;
  const op = { overlapped: _activeOps.size > 0 };
  for (const other of _activeOps) other.overlapped = true;
  _activeOps.add(op);
  const syncStats = () => ({
    syncs: _totalSyncs - syncsBefore,
    syncMs: Math.round(_totalSyncMs - syncMsBefore),
    ...(op.overlapped ? { overlapped: true } : {}),
  });
  try {
    const result = await fn();
    _activeOps.delete(op);
    const durationMs = Math.round((now() - start) * 100) / 100;
    const stats = syncStats();
    const details = detailsFromResult === undefined ? undefined : safeDetails(detailsFromResult, result);
    logWordOp({ ts: Date.now(), name, durationMs, details: _installed ? { ...stats, ...details } : details });
    if (_installed) _reportIfSlow(name, durationMs, stats.syncs, stats.syncMs, op.overlapped);
    return result;
  } catch (err) {
    _activeOps.delete(op);
    const durationMs = Math.round((now() - start) * 100) / 100;
    const stats = syncStats();
    logWordOp({
      ts: Date.now(),
      name,
      durationMs,
      error: true,
      details: { ...(_installed ? stats : {}), ...describeWordError(err) },
    });
    reportWordFailure(name, err, { durationMs, ...(_installed ? stats : {}) });
    throw err;
  }
}

function safeDetails<T>(fn: (result: T) => Record<string, unknown>, result: T): Record<string, unknown> | undefined {
  try {
    return fn(result);
  } catch (err) {
    return { detailsExtractionFailed: String(err) };
  }
}
